import { Effect, Schema } from 'effect';
import { VoiceInput } from './voice';

// Native TTS DTOs. These replace the `legacy-client.gen.ts` definitions — a
// one-time frozen snapshot of the retired Python backend's OpenAPI contract,
// where `alignment_timestamps` was widened to a
// `TtsTimestampMs | Record<string, unknown>` union. The effect-ts backend now
// derives its typesafe client directly from schemas like these, so the union is
// dead weight: runtime data is always the strict `{ start, end }` variant.
// Defined strictly and owned by hand. `identifier` annotations preserve the
// `TtsDetail` / `TtsTimestampMs` OpenAPI component names existing clients use.

/** Start and end timestamp (ms) for a single word in a TTS chunk. */
export const TtsTimestampMs = Schema.Struct({
  start: Schema.Int.annotate({
    description: 'Start time in milliseconds, counting from 0',
  }),
  end: Schema.Int.annotate({
    description: 'End time in milliseconds, counting from 0',
  }),
}).annotate({
  identifier: 'TtsTimestampMs',
  description: 'Start and end timestamp for a word in a TTS chunk.',
});
export type TtsTimestampMs = typeof TtsTimestampMs.Type;

/** Text-to-speech data for a given chunk of text. */
export const TtsDetail = Schema.Struct({
  id: Schema.String,
  text: Schema.String,
  url: Schema.String,
  /**
   * Deprecated for clients (`jsonSchema.deprecated`): TTS audio URLs are
   * permanent public S3 URLs, not signed/expiring URLs. The backend always sets
   * this to a far-future constant and no client consults it to drive
   * expiry/refetch logic. Kept for response-shape compatibility.
   */
  url_expires_at_ms: Schema.Int.annotate({
    description:
      'DEPRECATED: always a far-future constant; URLs are permanent public S3 URLs, not expiring. Do not rely on this field.',
    jsonSchema: { type: 'integer', deprecated: true },
  }),
  duration_ms: Schema.Int,
  format: Schema.Literal('mp3'),
  voice: Schema.String,
  voice_data_version: Schema.Int,
  alignment_timestamps: Schema.Array(TtsTimestampMs).annotate({
    description:
      'Start and end timestamps for each word in the text, in milliseconds',
  }),
}).annotate({
  identifier: 'TtsDetail',
  description: 'Text-to-speech data for a given chunk of text.',
});
export type TtsDetail = typeof TtsDetail.Type;

/** A single streamable chunk of text-to-speech content. */
export const TtsBatchTextResponseItem = Schema.Struct({
  text: Schema.String,
  tts_id: Schema.String.annotate({
    description:
      'DEPRECATED: legacy stored TTS chunk ID. New clients should use stateless synthesize responses directly.',
    jsonSchema: { deprecated: true },
  }),
}).annotate({
  identifier: 'CreateTtsBatchTextResponseItem',
  description:
    'DEPRECATED: legacy streamable chunk of text-to-speech content. Use POST /v2/synthesize for new clients.',
});
export type TtsBatchTextResponseItem = typeof TtsBatchTextResponseItem.Type;

export const SynthesizeDocPackRange = Schema.Tuple([
  Schema.Int,
  Schema.Int,
  Schema.Int,
]).annotate({
  identifier: 'SynthesizeDocPackRange',
  description: 'doc-pack range tuple: [page, charStart, charEnd].',
});
export type SynthesizeDocPackRange = typeof SynthesizeDocPackRange.Type;

// Payload caps. A single synthesize call is one player chunk (a handful of
// ranges / a paragraph of text); these bound abuse without constraining real
// use. The raw-text cap also bounds the reference-mode RESOLVED text — ranges
// resolve against the pack server-side, so the handler re-checks the resolved
// length against `SYNTHESIZE_MAX_TEXT_CHARS` (the payload cap alone can't).
export const SYNTHESIZE_MAX_RANGES = 256;
export const SYNTHESIZE_MAX_TEXT_CHARS = 10_000;

export const SynthesizePayload = Schema.Union([
  Schema.Struct({
    mode: Schema.Literal('reference').pipe(
      Schema.withDecodingDefaultType(Effect.sync(() => 'reference' as const)),
    ),
    doc: Schema.String,
    docPackVersion: Schema.Int,
    ranges: Schema.Array(SynthesizeDocPackRange).pipe(
      Schema.check(Schema.isMaxLength(SYNTHESIZE_MAX_RANGES)),
    ),
    voice: Schema.optional(VoiceInput),
  }),
  Schema.Struct({
    mode: Schema.Literal('raw-text'),
    text: Schema.String.pipe(
      Schema.check(Schema.isMaxLength(SYNTHESIZE_MAX_TEXT_CHARS)),
    ),
    voice: Schema.optional(VoiceInput),
  }),
]).annotate({
  identifier: 'SynthesizePayload',
  description:
    'Stateless synthesis request. Reference mode resolves text from an doc-pack; raw-text mode is a fallback/testing path.',
});
export type SynthesizePayload = typeof SynthesizePayload.Type;

export const SynthesizeWordTiming = Schema.Struct({
  start_ms: Schema.Int,
  end_ms: Schema.Int,
  page: Schema.NullOr(Schema.Int),
  char_start: Schema.Int,
  char_end: Schema.Int,
}).annotate({
  identifier: 'SynthesizeWordTiming',
  description:
    'Word timing keyed to doc-pack page/char offsets in reference mode, or submitted-text offsets in raw-text mode.',
});
export type SynthesizeWordTiming = typeof SynthesizeWordTiming.Type;

export const SynthesizeResponse = Schema.Struct({
  tts: TtsDetail,
  words: Schema.Array(SynthesizeWordTiming),
}).annotate({
  identifier: 'SynthesizeResponse',
  description: 'Stateless TTS response with audio details and offset timings.',
});
export type SynthesizeResponse = typeof SynthesizeResponse.Type;

export const OFFLINE_DOC_PACK_MAX_CHUNKS = 1_500;

export const OfflineDocPackManifestEntry = Schema.Struct({
  ranges: Schema.Array(SynthesizeDocPackRange),
  tts: TtsDetail,
  words: Schema.Array(SynthesizeWordTiming),
}).annotate({
  identifier: 'OfflineDocPackManifestEntry',
  description:
    'One chunk of an offline doc-pack download, in the order the client submitted it.',
});
export type OfflineDocPackManifestEntry =
  typeof OfflineDocPackManifestEntry.Type;
